import _ from 'lodash';
import { Arity, Filter, Operator } from './super-table';

export namespace Operators {
  export function arity(operator: Operator): Arity {
    return (<{ [T in Operator]: Arity }>{
      NONE: 'NULLARY',
      ANY_EQ: 'UNARY',
      NOT_ANY_EQ: 'UNARY',
      ALL_EQ: 'UNARY',
      NOT_ALL_EQ: 'UNARY',
      ANY_GT: 'UNARY',
      ALL_GT: 'UNARY',
      ANY_GTE: 'UNARY',
      ALL_GTE: 'UNARY',
      ANY_LT: 'UNARY',
      ALL_LT: 'UNARY',
      ANY_LTE: 'UNARY',
      ALL_LTE: 'UNARY',
      ANY_BETWEEN: 'BINARY',
      ALL_BETWEEN: 'BINARY',
      EMPTY: 'NULLARY',
      NOT_EMPTY: 'NULLARY',
      ROLLING: 'BINARY',
    })[operator];
  }
}

export namespace Filters {
  export function stringToStringFilter<T extends string>(
    val: string,
    default_: Filter<T>,
  ): Filter<T> {
    const parts = val.split('__');
    if (parts.length !== 2) {
      return default_;
    }
    const operator = parts[0].toUpperCase();
    const filter = decodeURIComponent(parts[1].trim());
    return {
      operator: operator as Operator,
      filter: filter as T,
    };
  }

  export function stringToMultiStringFilter<T extends string>(
    val: string,
    default_: Filter<T[]>,
  ): Filter<T[]> {
    const parts = val.split('__');
    if (parts.length !== 2) {
      return default_;
    }
    const operator = parts[0].toUpperCase();
    const filter = decodeURIComponent(parts[1].trim());
    return {
      operator: operator as Operator,
      filter: filter.length === 0 ? [] : (filter.split('::') as T[]),
    };
  }

  export function stringFilterToString<T extends string>(
    filter: Filter<T>,
  ): string {
    return `${filter.operator.toLowerCase()}__${
      filter.filter === null ? '' : encodeURIComponent(filter.filter)
    }`;
  }

  export function multiStringFilterToString<T extends string>(
    filter: Filter<T[]>,
  ): string {
    return `${filter.operator.toLowerCase()}__${
      filter.filter === null ? '' : encodeURIComponent(filter.filter.join('::'))
    }`;
  }

  export function stringToNumberFilter<T extends number>(
    val: string,
    default_: Filter<T>,
  ): Filter<T> {
    const parts = val.split('__');
    if (parts.length !== 2) {
      return default_;
    }
    const operator = parts[0].toUpperCase();
    const filter = parts[1];
    return {
      operator: operator as Operator,
      filter: +filter as T,
    };
  }

  export function stringToMultiNumberFilter<T extends number>(
    val: string,
    default_: Filter<T[]>,
  ): Filter<T[]> {
    const parts = val.split('__');
    if (parts.length !== 2) {
      return default_;
    }
    const operator = parts[0].toUpperCase();
    const filter = parts[1];
    return {
      operator: operator as Operator,
      filter:
        filter.length === 0 ? [] : (filter.split('::').map(v => +v) as T[]),
    };
  }

  export function numberFilterToString<T extends number>(
    filter: Filter<T>,
  ): string {
    return `${filter.operator.toLowerCase()}__${
      filter.filter === null ? '' : filter.filter
    }`;
  }

  export function multiNumberFilterToString<T extends number>(
    filter: Filter<T[]>,
  ): string {
    return `${filter.operator.toLowerCase()}__${
      filter.filter === null ? '' : filter.filter.join('::')
    }`;
  }

  export function encodedDateFilterToDateFilter<T extends string>(
    dateFilter: Filter<T[]>,
  ): Filter<T[]> {
    // If we didn't get two dates for an ANY BETWEEN, we probably have an old encoded filter.
    if (
      dateFilter.operator == 'ANY_BETWEEN' &&
      dateFilter.filter &&
      dateFilter.filter.length == 1
    ) {
      const seperatedDateFilter: T[] = decodeAnyBetweenDateFilter(
        dateFilter.filter[0],
      );
      // If we can't get a working set of dates, clear the filter.
      if (seperatedDateFilter.length == 0) {
        return {
          operator: 'NONE',
          filter: [],
        };
      }

      return {
        ...dateFilter,
        filter: seperatedDateFilter,
      };
    }
    return dateFilter;
  }

  export const noneOrAnyMultiStringFilterToString = (
    arr: string[] | undefined,
  ): string =>
    Filters.multiStringFilterToString({
      operator: (arr?.length ?? 0) > 0 ? 'ANY_EQ' : 'NONE',
      filter: (arr ?? []).map(n => n),
    });
}

export function decodeAnyBetweenDateFilter<T extends string>(
  singleStringDate: T,
) {
  const filteredPieces = decodeURIComponent(singleStringDate.trim());

  // Check if we can get two dates from any of the potential encodings.
  let seperatedDateFilter: T[] = [];
  if (filteredPieces.length > 0) {
    // Our existing filters are split by double colons.
    const doubleColonSplit = filteredPieces.split('::') as T[];
    if (doubleColonSplit.length == 2) {
      seperatedDateFilter = [...doubleColonSplit];
    } else {
      // Some of our old filters were split by commas.
      const commaSplit = filteredPieces.split(',') as T[];
      if (commaSplit.length == 2) {
        seperatedDateFilter = [...commaSplit];
      }
    }
  }
  return seperatedDateFilter;
}

export type FilterPrimitive = string | number | boolean | null | undefined;
export type FilterValue =
  | FilterPrimitive
  | FilterPrimitive[]
  | Filter<FilterPrimitive>
  | Filter<FilterPrimitive[]>;
/**
 * This is an attempt to generalize what a filter set should look like, though
 * it's not widely used due to TypeScript jankiness/issues.
 */
export type FilterSet = {
  [filterName: string]: FilterValue;
};

/**
 * If undefined, it will be assumed to be default values or URL query params.
 * Used by analytics to decide whether to log filter changes.
 * @see app-common/analytics.ts:logFilterChangeAnalytics()
 */
export interface FiltersSource {
  source: 'localStorage' | 'savedView' | 'byHand';
  /**
   * If the filters were applied by the default view "Default", savedViewId will
   * be 0, and savedViewName and savedViewIsShared will be undefined.
   */
  savedViewId?: number;
  savedViewName?: string | undefined;
  savedViewIsShared?: boolean | undefined;
}
/**
 * When a filter set contains a FiltersSource, this is what the key will be.
 * For example:
 * ```
 * {
 *   payerIds: { operator: 'ANY_EQ', filter: ['11', '22'] },
 *   assignedUserIds: ['33'],
 *   [filtersSourceKey]: { source: 'localStorage' },
 * }
 * ```
 */
export const filtersSourceKey = 'filtersSource';
/**
 * If this is set to false, the property filtersSource will be added as a hidden
 * property when a filter set is modified (by applying a filter manually, by
 * saved view, or on load from local storage). If this is set to true, it will
 * be added as a regular (visible) property.
 *
 * The current strategy is to have this as true (visible) so that filtersSource
 * is less likely to get lost during state propagation. Accordingly, any code
 * that compares two filter sets using `isEqual(a, b)` will need to ignore
 * filtersSource as part of that comparison.
 */
export const filtersSourceIsEnumerable = true;

/** Replace no-op filters with the default value. Useful for diffing. */
export function normalizeFilter<T>(
  filter: T | Filter<T>,
  defaultsForFilter: (T | Filter<T>)[],
): T | Filter<T> {
  // Some tables start with undefineds rather than the designated defaults.
  // Some input components also emit null when you start typing.
  if (filter === null || filter === undefined) {
    return defaultsForFilter[0];
  }
  // For nullary operators, any selected options are irrelevant.
  const operator = (<any>filter)?.operator as Operator | undefined;
  if (operator && Operators.arity(operator) === 'NULLARY') {
    return {
      ...filter,
      filter: (<any>defaultsForFilter[0])?.filter,
    };
  }
  return filter;
}

export function getActiveFilterNames<T extends { [filterName: string]: any }>(
  filterSet: T,
  defaultFilterSets: T[],
): string[] {
  const activeFilterNames: string[] = [];
  for (const [filterName, filter] of Object.entries(filterSet)) {
    const defaultsForFilter = defaultFilterSets.map(
      defaultFilterSet => defaultFilterSet[filterName],
    );

    const filterNormalized = normalizeFilter(filter, defaultsForFilter);

    const theFilterIsAtDefault = defaultsForFilter.some(defaultForFilter =>
      _.isEqual(filterNormalized, defaultForFilter),
    );

    if (!theFilterIsAtDefault) {
      activeFilterNames.push(filterName);
    }
  }
  return activeFilterNames;
}

export function diffTwoFilterSets<T extends { [filterName: string]: any }>(
  filterSet1: T,
  filterSet2: T,
  defaultFilterSets: T[],
) {
  const diff: {
    added: string[];
    modified: string[];
    removed: string[];
    unchanged: string[];
  } = {
    added: [],
    modified: [],
    removed: [],
    unchanged: [],
  };

  for (const filterName of new Set([
    ...Object.keys(filterSet1),
    ...Object.keys(filterSet2),
  ])) {
    const defaultsForFilter = defaultFilterSets.map(
      defaultFilterSet => (defaultFilterSet as any)[filterName],
    );

    const filter1Normalized = normalizeFilter(
      filterSet1[filterName],
      defaultsForFilter,
    );
    const filter2Normalized = normalizeFilter(
      filterSet2[filterName],
      defaultsForFilter,
    );

    if (
      _.isEqual(filter1Normalized, filter2Normalized) ||
      filter1Normalized == undefined ||
      filter2Normalized == undefined
    ) {
      diff.unchanged.push(filterName);
    } else {
      // The filter has changed; did it change to/from the default value?
      if (
        // Filter 1 is default and filter 2 is not
        defaultsForFilter.some(defaultForFilter =>
          _.isEqual(filter1Normalized, defaultForFilter),
        )
      ) {
        diff.added.push(filterName);
      } else if (
        // Filter 2 is default and filter 1 is not
        defaultsForFilter.some(defaultForFilter =>
          _.isEqual(filter2Normalized, defaultForFilter),
        )
      ) {
        diff.removed.push(filterName);
      } else {
        diff.modified.push(filterName);
      }
    }
  }

  return diff;
}

/**
 * If for example: `someFilter = 'custom'` and `customSomeFilter` exists, this
 * sets `someFilter = customSomeFilter` instead to be more cohesive for
 * analytics. Also removes all associated `customSomeFilter`s from the object
 * regardless.
 *
 * Returns a clone of the original object.
 */
export function simplifyCustomFiltersInFilterSet<T extends FilterSet>(
  filters: T,
): T {
  const friendlier = _.cloneDeep(filters);
  for (const [filterName, filterValue] of Object.entries(filters)) {
    const customFilterName =
      'custom' + filterName[0].toUpperCase() + filterName.slice(1);
    const customFilterValue = filters[customFilterName];
    if (filterValue === 'custom' && customFilterValue) {
      (<any>friendlier)[filterName] = customFilterValue;
    }
    if (customFilterValue) {
      delete friendlier[customFilterName];
    }
  }
  return friendlier;
}
